// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * React Query hooks for document operations.
 */
import { useQuery } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import { listDocuments, type ListDocumentsParams } from '../services'

/** Query key factory for document queries */
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (orgId: string, wsId: string, params?: ListDocumentsParams) =>
    [...documentKeys.lists(), orgId, wsId, params] as const,
}

interface UseDocumentsParams extends ListDocumentsParams {
  enabled?: boolean
}

/**
 * Fetch documents with optional filtering by group or slot.
 *
 * @example
 * ```tsx
 * // All documents
 * const { documents, isLoading } = useDocuments(apiClient, orgId, wsId)
 *
 * // Filter by group
 * const { documents } = useDocuments(apiClient, orgId, wsId, { groupId: 'REQ-123' })
 *
 * // Filter by slot
 * const { documents } = useDocuments(apiClient, orgId, wsId, { slotId: 'dpq' })
 * ```
 */
export function useDocuments(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  params?: UseDocumentsParams,
) {
  const { enabled = true, ...queryParams } = params ?? {}

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: documentKeys.list(orgId, wsId, queryParams),
    queryFn: () => listDocuments(apiClient, orgId, wsId, queryParams),
    enabled,
    staleTime: 30_000, // 30 seconds
  })

  return {
    documents: data?.documents ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading: isPending,
    isError,
    error,
    refetch,
  }
}
